import React, { useMemo, useState } from "react";

import { toast } from "@/design-system/components/Toast/Toast";

import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { Badge } from "@/design-system/components/Badge/Badge";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import { allowlistBuiltInProps } from "@/PHIAllowListPage/PHIAllowListEditModal";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";
import useCurrentProject from "@/hooks/use-project";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { AllowListType } from "@/PHIAllowListPage/types";
import { makeRequest } from "@/util";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import {
  allowlistCandidateKey,
  AllowlistStatus,
  buildAllowlistUpdate,
  canUnmask,
  getAllowlistCandidates,
  getAllowlistStatus,
  getPropertiesToAllow,
  isCandidateSelected,
} from "../allowlist";
import { EnableDestinationStepProps } from "../stepProps";

const linkClassName = "text-textActionSelected cursor-pointer underline";

export function AllowlistStep({ context, goNext }: EnableDestinationStepProps) {
  const {
    saasDestination: destination,
    configuredDestination,
    destinationsLoaded,
  } = context;
  const project = useCurrentProject();
  const canWrite = useUserHasAccess(
    RBACDomain.allowlist,
    RBACAccessLevel.write,
  );
  const { refetch: refetchDestinations } = useListAllDestinationsForEnv();
  const [saving, setSaving] = useState(false);
  const [selectionOverrides, setSelectionOverrides] = useState<
    Record<string, boolean>
  >({});

  const candidates = useMemo(
    () => (destination ? getAllowlistCandidates(destination.key) : []),
    [destination],
  );
  const statuses = useMemo(() => {
    const result = new Map<string, AllowlistStatus>();
    if (configuredDestination && project.loaded) {
      for (const candidate of candidates) {
        result.set(
          allowlistCandidateKey(candidate),
          getAllowlistStatus(candidate, project, configuredDestination),
        );
      }
    }
    return result;
  }, [candidates, configuredDestination, project]);
  const toAllow = useMemo(
    () =>
      configuredDestination && project.loaded
        ? getPropertiesToAllow({
            candidates,
            project,
            destination: configuredDestination,
            selectionOverrides,
          })
        : [],
    [candidates, configuredDestination, project, selectionOverrides],
  );

  const isResolving =
    !project.loaded || (!configuredDestination && !destinationsLoaded);
  const isMissing = !isResolving && !configuredDestination;

  async function handleApply() {
    if (!destination || !configuredDestination) {
      return;
    }
    if (toAllow.length === 0) {
      goNext();
      return;
    }
    setSaving(true);
    const levels = buildAllowlistUpdate(configuredDestination, toAllow);
    try {
      for (const type of Object.values(AllowListType)) {
        await makeRequest("update_destination_phi_allowlist", {
          destination_type: destination.key,
          sensitivity_type: type,
          sensitivity_levels: levels[type],
        });
      }
      await refetchDestinations();
      goNext();
    } catch (error) {
      toast.error("Something went wrong. Unable to save allowlist.");
      console.error(error);
      setSaving(false);
    }
  }

  function toggle(key: string, checked: boolean) {
    setSelectionOverrides((current) => ({ ...current, [key]: checked }));
  }

  const name = destination?.name ?? "this destination";

  function renderBody() {
    if (isResolving) {
      return <LoadingSpinner className="size-6" />;
    }
    if (isMissing) {
      return (
        <Banner type="error">
          We couldn’t find the {name} configuration. Go back and connect {name}{" "}
          again.
        </Banner>
      );
    }
    return (
      <>
        <Typography variant="body-sm" color="subtle">
          Check each property to send it to {name}. Make sure your
          organization’s HIPAA policies allow sending them. Refer to the{" "}
          {destination?.docsURL && (
            <>
              <a
                className={linkClassName}
                href={destination.docsURL}
                rel="noopener noreferrer"
                target="_blank"
              >
                {name} docs
              </a>{" "}
              and the{" "}
            </>
          )}
          <a
            className={linkClassName}
            href="https://documentation.freshpaint.io/readme/hipaa-mode#enforced-allowlists"
            rel="noopener noreferrer"
            target="_blank"
          >
            HIPAA Mode enforced allowlists docs
          </a>{" "}
          for more details.
        </Typography>
        <ul className="border-borderSubtle divide-borderSubtle divide-y rounded-lg border">
          {candidates.map((candidate) => {
            const key = allowlistCandidateKey(candidate);
            const status = statuses.get(key) ?? "notAllowed";
            const id = `allowlist-${key}`;
            const unmaskable = canUnmask(candidate, status);
            const unmasking = unmaskable && selectionOverrides[key] === true;
            return (
              <li key={key} className="flex items-start gap-3 p-4">
                <Checkbox
                  id={id}
                  checked={
                    unmaskable
                      ? unmasking
                      : status !== "notAllowed" ||
                        isCandidateSelected(candidate, selectionOverrides)
                  }
                  disabled={
                    (status !== "notAllowed" && !unmaskable) ||
                    canWrite === false
                  }
                  onCheckedChange={(checked) => toggle(key, checked === true)}
                />
                <label
                  htmlFor={id}
                  className="flex flex-1 cursor-pointer flex-col gap-1"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-label-sm text-textPrimary">
                      {allowlistBuiltInProps[candidate.property]
                        ?.beautifulName ?? candidate.property}
                    </span>
                    <Badge
                      text={
                        candidate.requirement === "required"
                          ? "Required"
                          : "Recommended"
                      }
                      variant={
                        candidate.requirement === "required"
                          ? "warning"
                          : "info"
                      }
                    />
                    {status === "allowed" && (
                      <Badge text="Already allowed" variant="success" />
                    )}
                    {status === "hashed" && <Badge text="Sent hashed" />}
                  </span>
                  <span className="text-body-xs text-textTertiary">
                    {candidate.description}
                  </span>
                  {unmaskable && (
                    <span
                      className={
                        unmasking
                          ? "text-body-xs text-textError"
                          : "text-body-xs text-textTertiary"
                      }
                    >
                      {unmasking
                        ? `This property will be sent to ${name} unhashed. Keep it checked only if your agreement with ${name} covers this data.`
                        : `${name} needs this property unhashed to turn on. Check it to send it unhashed.`}
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      </>
    );
  }

  return (
    <WizardStepLayout
      title="Allowlist properties"
      description={`HIPAA mode only sends allowlisted properties. These are the properties ${name} needs or recommends.`}
      skippable
      primaryAction={{
        label:
          toAllow.length > 0
            ? `Allow ${toAllow.length} ${toAllow.length === 1 ? "property" : "properties"}`
            : "Continue",
        onClick: () => void handleApply(),
        loading: saving,
        disabled:
          isResolving ||
          isMissing ||
          (canWrite === false && toAllow.length > 0),
        disabledReason: isMissing
          ? `Connect ${name} first`
          : "Ask an admin for permission to edit allowlists",
      }}
    >
      {canWrite === false && (
        <Banner type="warning">
          You don’t have permission to edit allowlists. Ask an admin to finish
          this step, or skip it for now.
        </Banner>
      )}
      {renderBody()}
    </WizardStepLayout>
  );
}
